import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { filterByCharMode } from '@/kit/inputCharMode';
import { validateForm } from '@/features/manage/formRules';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { groupService } from '@/core/services/group';
import { phase1Service } from '@/core/services/phase1';
import type { ReportingCodeNode } from '@/core/models/phase1.models';
import {
  GroupJournalStatus,
  type GroupJournalRow,
  type GroupJournalTypeOption,
  type GroupStructureView,
  type GroupSummaryRow,
  type GroupUndertakingRow,
  type SaveGroupJournalLine,
} from '@/core/models/group.models';
import { decimalPipe } from './format';
import { useMounted } from './useMounted';
import './TaxreturnGroupJournals.scss';

/** One editable line of the group journal being drafted (`key` only identifies the row while editing). */
interface JournalLineForm {
  key: number;
  reportingCodeId: string | null;
  groupUndertakingId: string | null;
  debit: number | null;
  credit: number | null;
  narrative: string | null;
  /** The amount boxes keep what was typed ("12." must stay "12."); the numbers above are what is saved. */
  debitText?: string;
  creditText?: string;
}

/** The longest narrative a group journal or its lines hold (NVARCHAR(500)). */
const NARRATIVE_MAX = 500;

/** Amount columns are DECIMAL(19,4): 15 whole digits and 4 decimals. */
const cleanAmount = (text: string): string =>
  filterByCharMode(text, { charMode: 'number', allowDecimal: true, maxIntDigits: 15, maxDecimals: 4 });

interface ReportingCodeOption {
  reportingCodeId: string;
  label: string;
}

/** Angular's `type="number"` `ngModel`: a blank box is null, anything else is parsed. */
const numberOrNull = (text: string): number | null => {
  if (text === '') return null;
  const value = parseFloat(text);
  return Number.isNaN(value) ? null : value;
};

export function TaxreturnGroupJournals() {
  const mounted = useMounted();
  const nextKey = useRef(1);

  const [groups, setGroups] = useState<GroupSummaryRow[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [structure, setStructure] = useState<GroupStructureView | null>(null);
  const [journalTypes, setJournalTypes] = useState<GroupJournalTypeOption[]>([]);
  /**
   * #155: the codes a line can post to: the parent period's full reporting-code list (postable nodes, flattened,
   * in code order).
   */
  const [reportingCodes, setReportingCodes] = useState<ReportingCodeOption[]>([]);
  const [journals, setJournals] = useState<GroupJournalRow[]>([]);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editingJournalId, setEditingJournalId] = useState<string | null>(null);
  const [typeCode, setTypeCode] = useState('');
  const [journalDate, setJournalDate] = useState('');
  const [narrative, setNarrative] = useState('');
  const [lines, setLines] = useState<JournalLineForm[]>([]);

  const blankLine = (): JournalLineForm => ({
    key: nextKey.current++,
    reportingCodeId: null,
    groupUndertakingId: null,
    debit: 0,
    credit: 0,
    narrative: null,
  });

  const totalDebit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
  /** A convenience check only; the stored procedure makes the authoritative one. */
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.005;

  const cancelEdit = () => {
    setIsEditing(false);
    setEditingJournalId(null);
    setLines([]);
  };

  const loadReportingCodes = useCallback(
    (periodId: string) => {
      phase1Service
        .getReportingCodes(periodId)
        .then((tree) => {
          if (!mounted.current) return;
          const flat: ReportingCodeNode[] = [];
          const walk = (nodes: ReportingCodeNode[]) =>
            nodes.forEach((n) => {
              if (n.isPostable) {
                flat.push(n);
              }
              walk(n.children ?? []);
            });
          walk(tree);
          setReportingCodes(
            flat
              .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
              .map((n) => ({ reportingCodeId: n.reportingCodeId, label: `${n.code} - ${n.description}` })),
          );
        })
        .catch(() => {
          if (mounted.current) setReportingCodes([]);
        });
    },
    [mounted],
  );

  const load = useCallback(
    (groupId: string | null) => {
      if (!groupId) return;
      setIsEditing(false);
      setEditingJournalId(null);
      setLines([]);

      groupService
        .getGroup(groupId)
        .then((result) => {
          if (!mounted.current) return;
          setStructure(result);
          setJournalTypes(result.journalTypes);
          loadReportingCodes(result.group.parentAccountingPeriodId);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });

      groupService
        .getJournals(groupId)
        .then((result) => {
          if (mounted.current) setJournals(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [mounted, loadReportingCodes],
  );

  useEffect(() => {
    const client = selectedClient();
    groupService
      .getGroups(client?.id ?? null)
      .then((result) => {
        if (!mounted.current) return;
        setGroups(result);
        const first = result.length > 0 ? result[0].groupPeriodId : null;
        setSelectedGroupId(first);
        if (first) {
          load(first);
        }
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newJournal = () => {
    setIsEditing(true);
    setSubmitted(false);
    setEditingJournalId(null);
    setTypeCode(journalTypes.length > 0 ? journalTypes[0].typeCode : '');
    const group = groups.find((g) => g.groupPeriodId === selectedGroupId);
    setJournalDate(group ? group.endDate : '');
    setNarrative('');
    setLines([blankLine(), blankLine()]);
  };

  const editJournal = (row: GroupJournalRow) => {
    if (row.status !== GroupJournalStatus.Draft) return;

    groupService
      .getJournal(row.groupJournalId)
      .then((view) => {
        if (!mounted.current) return;
        setIsEditing(true);
        setSubmitted(false);
        setEditingJournalId(view.journal.groupJournalId);
        setTypeCode(view.journal.typeCode);
        setJournalDate(view.journal.journalDate);
        setNarrative(view.journal.narrative ?? '');
        setLines(
          view.lines.map((l) => ({
            key: nextKey.current++,
            reportingCodeId: l.reportingCodeId,
            groupUndertakingId: l.groupUndertakingId,
            debit: l.debit,
            credit: l.credit,
            narrative: l.narrative,
          })),
        );
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const updateLine = (key: number, patch: Partial<JournalLineForm>) =>
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const save = () => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    if (saving) return;

    setSubmitted(true);
    if (!journalDate) {
      validateForm(false);
      return;
    }

    // #156: a line with an amount but no reporting code used to be dropped silently; say what is missing instead.
    const incomplete = lines.some(
      (l) => !l.reportingCodeId && ((Number(l.debit) || 0) !== 0 || (Number(l.credit) || 0) !== 0),
    );
    if (incomplete) {
      notifyError('Choose a reporting code on every line that has an amount.');
      return;
    }
    const saved: SaveGroupJournalLine[] = lines
      .filter((l) => l.reportingCodeId)
      .map((l, index) => ({
        lineNo: index + 1,
        reportingCodeId: l.reportingCodeId as string,
        groupUndertakingId: l.groupUndertakingId,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        narrative: l.narrative,
      }));

    if (saved.length < 2) {
      notifyError('A group journal needs at least two lines, each with a reporting code.');
      return;
    }

    setSaving(true);
    groupService
      .saveJournal({
        groupPeriodId: selectedGroupId,
        groupJournalId: editingJournalId,
        typeCode,
        journalDate,
        narrative: narrative || null,
        lines: saved,
      })
      .then(() => {
        if (!mounted.current) return;
        notifySuccess('The group journal has been saved.');
        load(selectedGroupId);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setSaving(false);
      });
  };

  const post = (row: GroupJournalRow) => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    groupService
      .postJournal({
        groupPeriodId: selectedGroupId,
        groupJournalId: row.groupJournalId,
        rowVersion: row.rowVersion,
      })
      .then(() => {
        if (!mounted.current) return;
        notifySuccess('The group journal has been posted.');
        load(selectedGroupId);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const reverse = (row: GroupJournalRow) => {
    if (!selectedGroupId) {
      notifyError('Select a group first.');
      return;
    }
    groupService
      .reverseJournal({ groupPeriodId: selectedGroupId, groupJournalId: row.groupJournalId })
      .then(() => {
        if (!mounted.current) return;
        notifySuccess('The group journal has been reversed.');
        load(selectedGroupId);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const deleteJournal = (row: GroupJournalRow) => {
    groupService
      .deleteJournal(row.groupJournalId)
      .then(() => {
        if (!mounted.current) return;
        notifySuccess('The draft group journal has been deleted.');
        load(selectedGroupId);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const columns: DataTableColumn<GroupJournalRow>[] = [
    { id: 'journalNo', header: 'No.', cell: (row) => row.journalNo },
    { id: 'type', header: 'Type', cell: (row) => row.typeDescription },
    { id: 'date', header: 'Date', cell: (row) => row.journalDate },
    { id: 'narrative', header: 'Narrative', cell: (row) => row.narrative },
    {
      id: 'debit',
      header: 'Debit',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => decimalPipe(row.totalDebit),
    },
    {
      id: 'credit',
      header: 'Credit',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => decimalPipe(row.totalCredit),
    },
    { id: 'status', header: 'Status', cell: (row) => row.statusLabel },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => (
        <>
          {row.status === GroupJournalStatus.Draft && (
            <Tooltip title="Edit">
              <Button onClick={() => editJournal(row)}>
                <Icon name="edit" outlined />
              </Button>
            </Tooltip>
          )}
          {row.status === GroupJournalStatus.Draft && (
            <Tooltip title="Post">
              <Button onClick={() => post(row)}>
                <Icon name="check_circle" outlined />
              </Button>
            </Tooltip>
          )}
          {row.status === GroupJournalStatus.Posted && (
            <Tooltip title="Reverse">
              <Button onClick={() => reverse(row)}>
                <Icon name="undo" outlined />
              </Button>
            </Tooltip>
          )}
          {row.status === GroupJournalStatus.Draft && (
            <Tooltip title="Delete">
              <Button onClick={() => deleteJournal(row)}>
                <Icon name="delete_outline" outlined />
              </Button>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const undertakings: GroupUndertakingRow[] = structure ? structure.undertakings : [];

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-group-journals">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" disabled={!selectedGroupId} onClick={newJournal}>
              <Icon name="add" />
              <span>Group Journal</span>
            </Button>
            <Select<GroupSummaryRow>
              className="w-300 ml-1"
              openOnFocus
              panelClass="myPanelClass"
              items={groups}
              bindLabel="groupName"
              bindValue="groupPeriodId"
              clearable={false}
              value={selectedGroupId}
              onChange={(value) => {
                const groupId = value as string | null;
                setSelectedGroupId(groupId);
                load(groupId);
              }}
            />
          </div>
        </div>

        {isEditing && (
          <div className="mb-2 edit-panel">
            <div className="d-flex mb-1">
              <Select<GroupJournalTypeOption>
                className="w-250"
                openOnFocus
                panelClass="myPanelClass"
                items={journalTypes}
                bindLabel="description"
                bindValue="typeCode"
                clearable={false}
                value={typeCode}
                onChange={(value) => setTypeCode(value as string)}
              />
              <InputField
                fieldClassName="ml-1"
                type="date"
                invalid={submitted && !journalDate}
                value={journalDate}
                onChange={(event) => setJournalDate(event.target.value)}
              />
              <RequiredMark />
              <InputField
                fieldClassName="ml-1 w-300"
                placeholder="Narrative"
                maxLength={NARRATIVE_MAX}
                value={narrative}
                onChange={(event) => setNarrative(event.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="line-table-wrapper">
              <table className="line-table">
                <tbody>
                  <tr>
                    <th>Reporting code</th>
                    <th>Undertaking (blank = group adjustment)</th>
                    <th className="text-right">Debit</th>
                    <th className="text-right">Credit</th>
                    <th>Narrative</th>
                    <th></th>
                  </tr>
                  {lines.map((line) => (
                    <tr key={line.key}>
                      <td>
                        <Select<ReportingCodeOption>
                          className="w-250"
                          panelClass="myPanelClass"
                          items={reportingCodes}
                          bindLabel="label"
                          bindValue="reportingCodeId"
                          value={line.reportingCodeId}
                          onChange={(value) => updateLine(line.key, { reportingCodeId: value as string | null })}
                        />
                      </td>
                      <td>
                        <Select<GroupUndertakingRow>
                          className="w-250"
                          panelClass="myPanelClass"
                          items={undertakings}
                          bindLabel="clientName"
                          bindValue="groupUndertakingId"
                          value={line.groupUndertakingId}
                          onChange={(value) => updateLine(line.key, { groupUndertakingId: value as string | null })}
                        />
                      </td>
                      <td className="text-right">
                        <input
                          className="mat-mdc-input-element"
                          inputMode="decimal"
                          value={line.debitText ?? line.debit ?? ''}
                          onChange={(event) => {
                            const text = cleanAmount(event.target.value);
                            updateLine(line.key, { debit: numberOrNull(text), debitText: text });
                          }}
                        />
                      </td>
                      <td className="text-right">
                        <input
                          className="mat-mdc-input-element"
                          inputMode="decimal"
                          value={line.creditText ?? line.credit ?? ''}
                          onChange={(event) => {
                            const text = cleanAmount(event.target.value);
                            updateLine(line.key, { credit: numberOrNull(text), creditText: text });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="mat-mdc-input-element"
                          autoComplete="off"
                          maxLength={NARRATIVE_MAX}
                          value={line.narrative ?? ''}
                          onChange={(event) => updateLine(line.key, { narrative: event.target.value })}
                        />
                      </td>
                      <td>
                        <Button title="Delete" onClick={() => setLines((current) => current.filter((l) => l.key !== line.key))}>
                          <Icon name="delete_outline" outlined />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex mt-1">
              <Button onClick={() => setLines((current) => [...current, blankLine()])}>
                <Icon name="add" /> Add line
              </Button>
              <p className="ml-1 mb-0">
                Debits {decimalPipe(totalDebit)} / Credits {decimalPipe(totalCredit)}{' '}
                <span className={isBalanced ? 'text-success' : 'text-danger'}>
                  {isBalanced ? '(balanced)' : '(does not balance)'}
                </span>
              </p>
            </div>

            <div className="mt-1">
              <Button className="action-button primary-button" busy={saving} onClick={save}>
                Save draft
              </Button>
              <Button className="action-button secondary-button ml-1" onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable columns={columns} rows={journals} rowKey={(row) => row.groupJournalId} />
        </div>
      </div>
    </SideMenuPage>
  );
}
